import { useActor } from '@xstate/react';
import { omit } from 'lodash';
import { FC, useState } from 'react';

import { Address } from 'api/api';

import { useGlobalState } from '~src/global-state';

import { AddressCardUI } from '~address-form/address-list/address-card';

import { AddressFormBusinessProvider } from '../context/use-address-form';
import { BillingFormFlowProvider, useBillingFormFlow } from '../context/use-billing-form-flow';
import { BillingFormModalProxy } from '../form-templates/billing-form-modal';
import { DeliveryAddressFormWithSuggestion } from '../form-templates/delivery-address-form-with-suggestion';
import { VerifyAddressModalProxy } from '../form-templates/verify-address-modal';
import { isStreetNumberMissing } from '../inputs/autocomplete/autocomplete-helpers';
import { BillingAddressCheckbox } from './billing-address-checkbox';
import { BillingAddressManagerLayout } from './billing-address-manager-layout';
import { getBillingAddressCandidate } from './utils';

export const BillingAddressRendererFSM: FC = () => {
  const globalState = useGlobalState();
  const { billingFlowService, editInitialAddress } = useBillingFormFlow();
  const [state, send] = useActor(billingFlowService);
  const [extendedAddress, setExtendedAddress] = useState<Address>();

  const optionalStreetNumber = globalState.address_form_configuration?.layout.fields.find(
    field => field.name === 'address_lines' && field.type === 'optional_address_line_street_number'
  );

  const getCheckoutStage = () => {
    switch (true) {
      case state.matches('checkout.billingSummary'):
        return (
          <>
            <AddressCardUI
              handleClick={() => send({ type: 'BUTTON_CLICK' })}
              address={
                globalState.billing_address || globalState.address_book?.addresses?.[1] || {}
              } // TODO check if we don't need to circumvent this
              boldenFirstLine
            />
            <BillingAddressCheckbox
              id="billing-address-checkbox"
              checked={!state.matches('checkbox.unchecked')}
              onChange={() => send({ type: 'CHECK', data: { address: globalState.recipient! } })}
            />
          </>
        );
      case state.matches('checkout.inlineAddressForm'):
        return (
          <>
            <BillingAddressCheckbox
              id="billing-address-checkbox"
              checked={!state.matches('checkbox.unchecked')}
              onChange={() => send({ type: 'CHECK' })}
            />
            <AddressFormBusinessProvider
              onSubmit={(address, onError) => {
                send({ type: 'SUBMIT', data: { address, onError } });
              }}
              address={
                editInitialAddress
                  ? globalState.billing_address
                  : globalState.prefill_billing || omit(globalState.recipient, ['id'])
              }
              isBillingAddress
              // check if billing address is verified if enable_extended_validation is turned on
              addressVerified={
                globalState.address_form_configuration?.global_features?.enable_extended_validation
                  ? editInitialAddress
                  : true
              }
              isModalForm={false}
              extendedAddress={extendedAddress}
              setExtendedAddress={setExtendedAddress}
            >
              <DeliveryAddressFormWithSuggestion
                addressFlowService={billingFlowService}
                triggerExtendedValidation={data =>
                  send({
                    type: 'SHOW_EXTENDED_VALIDATION_MODAL',
                    data,
                  })
                }
              />
            </AddressFormBusinessProvider>
          </>
        );
      case state.matches('checkout.billingSameAsRecipient'):
      default:
        return (
          <BillingAddressCheckbox
            id="billing-address-checkbox"
            checked={state.matches('checkbox.checked')}
            onChange={() =>
              send({
                type: 'UNCHECK',
                data: {
                  address: getBillingAddressCandidate(
                    globalState.address_book?.addresses,
                    globalState.recipient
                  )!,
                },
              })
            }
          />
        );
    }
  };

  const getModalStage = () => {
    switch (true) {
      case state.matches('modal.modalAddressList'):
      case state.matches('modal.modalAddressForm'):
        return (
          <BillingFormModalProxy
            step={state.matches('modal.modalAddressList') ? 'list' : 'form'}
            triggerExtendedValidation={data =>
              send({
                type: 'SHOW_EXTENDED_VALIDATION_MODAL',
                data,
              })
            }
            handleClose={() => billingFlowService.send({ type: 'CLOSE' })}
            handleSubmit={address => billingFlowService.send({ type: 'SUBMIT', data: { address } })}
            handleDelete={address => billingFlowService.send({ type: 'DELETE', data: { address } })}
            handleAddNewAddress={() =>
              billingFlowService.send({ type: 'ADD_NEW_ADDRESS', data: { address: {} } })
            }
            handleOpenAddress={address =>
              billingFlowService.send({ type: 'OPEN_ADDRESS', data: { address } })
            }
            extendedAddress={extendedAddress}
            setExtendedAddress={setExtendedAddress}
          />
        );
      case state.matches('modal.extendedValidationModal'):
        return (
          <VerifyAddressModalProxy
            enteredAddress={state.context.extendedValidation.address}
            suggestion={state.context.extendedValidation.suggestion}
            isSubmissionBlocking={state.context.extendedValidation.isSubmissionBlocking}
            handleClose={() => send({ type: 'CLOSE', data: { shouldResetForm: true } })}
            handlePrimaryButton={address => {
              if (optionalStreetNumber && isStreetNumberMissing(address.address_lines?.[0] || '')) {
                send({ type: 'CLOSE', data: { shouldResetForm: true } });
                setExtendedAddress(state.context.extendedValidation.suggestion?.address);
              } else {
                send({ type: 'SUBMIT_ADDRESS', data: { address } });
              }
            }}
            handleSecondaryButton={address =>
              state.context.extendedValidation.isSubmissionBlocking
                ? send({ type: 'CLOSE', data: { shouldResetForm: true } })
                : send({ type: 'SUBMIT_ADDRESS', data: { address } })
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {getCheckoutStage()}
      {getModalStage()}
    </>
  );
};

export const BillingAddressManagerFSM: React.FC = () => {
  return (
    <BillingFormFlowProvider>
      <BillingAddressManagerLayout>
        <BillingAddressRendererFSM />
      </BillingAddressManagerLayout>
    </BillingFormFlowProvider>
  );
};
